import { useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";
import API from "../../API";
import { decodeAgreementHtml } from "../../components/Controls/agreementContent";

const TYPE_IDS = {
  TermsOfService: 1,
  PrivacyPolicy: 2,
};

/**
 * Public marketing page that loads the active agreement for a type
 * (GET /Agreements/ByType/{typeId}/Current) and renders decoded HTML.
 */
export default function PublicAgreementPage({
  typeId,
  fallbackTitle,
  fallbackLead,
}) {
  const [agreement, setAgreement] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await API.getAsync(`/Agreements/ByType/${typeId}/Current`);
        if (cancelled) return;
        if (resp?.ok && resp.jsonData) {
          setAgreement(resp.jsonData);
        } else {
          setError("This document is temporarily unavailable.");
          setAgreement(null);
        }
      } catch {
        if (!cancelled) {
          setError("This document is temporarily unavailable.");
          setAgreement(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [typeId]);

  const title =
    agreement?.Name || agreement?.name || agreement?.TypeName || fallbackTitle;
  const version =
    agreement?.Version ||
    (agreement?.MajorVersion != null
      ? `${agreement.MajorVersion}.${agreement.MinorVersion ?? 0}`
      : null);
  const html = useMemo(() => {
    const raw = agreement?.Content || agreement?.content || "";
    return decodeAgreementHtml(raw);
  }, [agreement]);

  return (
    <article className="mkt-page">
      <header className="mkt-hero">
        <h1>{title}</h1>
        {fallbackLead ? <p>{fallbackLead}</p> : null}
        {version ? (
          <p className="fieldHint" data-testid="public-agreement-version">
            Version {version}
          </p>
        ) : null}
      </header>

      <section className="mkt-section">
        {loading ? <p>Loading…</p> : null}
        {!loading && error ? <p>{error}</p> : null}
        {!loading && !error && html ? (
          <div className="agreementContent public-agreement-body">
            {looksLikeMarkup(html) ? parse(html) : <pre>{html}</pre>}
          </div>
        ) : null}
      </section>
    </article>
  );
}

function looksLikeMarkup(s) {
  return typeof s === "string" && /<\/?[a-z][\s\S]*>/i.test(s);
}

export { TYPE_IDS };
